import { Trans } from "@lingui/react/macro";
import { LayoutTemplate, Moon, Sun } from "lucide-react";
import { observer } from "mobx-react-lite";
import { CSSProperties } from "react";

import { devTools, gitBranch, gitCommit } from "@/config/config";

import { Button } from "../components/ui-kit/button";
import { AppModel } from "./app-model";
import { LocaleSwitcher } from "./layout/locale-switcher";

export const Footer = observer(function Footer({ app }: { app: AppModel }) {
  return (
    <footer className="text-xs mb-5 flex flex-col items-start">
      <div className="mb-1 flex items-center gap-2">
        <Button variant="subtle" size="sm" onClick={() => app.toggleDarkMode()}>
          {app.darkMode ? (
            <Sun className="size-3.5" />
          ) : (
            <Moon className="size-3.5" />
          )}
          {app.darkMode ? <Trans>Light mode</Trans> : <Trans>Dark mode</Trans>}
        </Button>

        <Button
          variant="subtle"
          size="sm"
          onClick={() => app.toggleLegacyLayout()}
        >
          <LayoutTemplate className="size-3.5" />
          {app.legacyLayout ? (
            <Trans>New layout</Trans>
          ) : (
            <Trans>Classic layout</Trans>
          )}
        </Button>

        <LocaleSwitcher />
      </div>

      {/* Until the CLV portal itself is certified. */}
      <p className="mt-1 max-w-xl text-muted-foreground italic">
        <Trans>
          Informational only. The CLV web portal is not a certified medical
          device. HeartCore trends and measurements come from the CE-marked
          HeartCore device (MDR Class IIb).
        </Trans>
      </p>

      <div data-mask style={{ "--mask-width": "400px" } as CSSProperties}>
        <Trans>CLV version:</Trans> {app.clvVersion}
        {devTools && gitBranch && (
          <>
            {" "}
            |{" "}
            <span
              className={
                gitBranch !== "main"
                  ? "text-warning-foreground font-semibold"
                  : ""
              }
            >
              {gitBranch}
            </span>{" "}
            ({gitCommit})
          </>
        )}
        {devTools && (
          <>
            {" "}
            |{" "}
            <button
              type="button"
              className="bg-transparent border-0 p-0 text-inherit underline hover:text-foreground/80 cursor-pointer"
              onClick={() => app.setDevToolsOpen(true)}
            >
              Dev tools (F2)
            </button>
          </>
        )}
        <br className="h-5" />
        <Trans>API environment:</Trans>{" "}
        {app.seerlinqApi.apiInfo ? app.seerlinqApi.apiInfo.environment : ""} |{" "}
        <Trans>API version:</Trans>{" "}
        {app.seerlinqApi.apiInfo ? app.seerlinqApi.apiInfo.version : ""}
        <br className="h-10" />
      </div>

      <div>
        Copyright &copy;{" "}
        <span data-mask style={{ "--mask-width": "35px" } as CSSProperties}>
          {/* eslint-disable-next-line no-restricted-syntax -- copyright year, not a server timestamp */}
          {new Date().getFullYear()}
        </span>{" "}
        Seerlinq
        <br className="h-10" />
        C'est La Vie
      </div>
    </footer>
  );
});
